import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CalendarDays, Check, Sparkles, X } from 'lucide-react';
import type {
  ServiceCatalogAddOn,
  ServiceCatalogOffering,
  ServiceCatalogService,
  ServiceCatalogTier,
} from '../../lib/clientServices';

export type ServiceConfigSubmission = {
  serviceCategory?: string;
  serviceCode?: string;
  serviceName?: string;
  tierSkuId?: string;
  tierName?: string;
  currentServiceId?: string;
  requestedAction?: 'purchase' | 'update' | 'upgrade';
  paymentMethodId?: string;
  prorationDate?: number;
  cartItems: Array<{ skuId: string; quantity: number }>;
  laneSelections?: Array<{
    tierSkuId: string;
    laneIds: string[];
    laneLabels?: string[];
  }>;
  title?: string;
  summary?: string;
  proposalType?: string;
  expiresAt?: string;
  discountCode?: string;
  configurationSnapshot: Record<string, unknown>;
};

type ServiceConfigDrawerProps = {
  open: boolean;
  mode: 'checkout' | 'proposal';
  orgName: string;
  services: ServiceCatalogService[];
  submitting?: boolean;
  submitLabel?: string;
  initialServiceCode?: string | null;
  initialTierSkuId?: string | null;
  initialOfferingLabels?: string[];
  initialAddOnNames?: string[];
  initialTitle?: string | null;
  initialSummary?: string | null;
  initialProposalType?: string | null;
  onClose: () => void;
  onSubmit: (payload: ServiceConfigSubmission) => Promise<void> | void;
};

const Overlay = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  padding: 0;
  background: rgba(4, 6, 10, 0.56);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 180;
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: clamp(420px, 34vw, 540px);
  height: 100dvh;
  background: linear-gradient(180deg, rgba(17, 20, 28, 0.992), rgba(12, 15, 22, 0.992));
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: -18px 0 42px rgba(0, 0, 0, 0.36);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  transform: translateX(${({ $open }) => ($open ? '0%' : '104%')});
  transition: transform 180ms ease;
  z-index: 190;

  @media (max-width: 860px) {
    width: min(100vw, 100%);
  }
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const HeaderTitle = styled.div`
  color: #f3f4f6;
  font-size: 15px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const HeaderSub = styled.div`
  margin-top: 4px;
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: #f3f4f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: grid;
  align-content: start;
  gap: 18px;
`;

const Footer = styled.div`
  display: grid;
  gap: 10px;
  padding: 14px 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const Tabs = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const TabButton = styled.button<{ $active: boolean }>`
  position: relative;
  min-height: 38px;
  padding: 0 14px;
  border: 0;
  background: transparent;
  color: ${({ $active }) => ($active ? '#f3f4f6' : '#9aa4b2')};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: -1px;
    height: 2px;
    border-radius: 999px;
    background: ${({ $active }) => ($active ? 'rgba(224,161,94,.92)' : 'transparent')};
  }
`;

const Section = styled.section`
  display: grid;
  gap: 10px;
`;

const SectionLabel = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.22px;
  text-transform: uppercase;
`;

const SectionCopy = styled.div`
  color: #cbd3df;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
`;

const TierList = styled.div`
  display: grid;
  gap: 10px;
`;

const TierCard = styled.button<{ $active: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 16px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224,161,94,.38)' : 'rgba(255,255,255,.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224,161,94,.1)' : 'rgba(255,255,255,.03)')};
  padding: 14px;
  display: grid;
  gap: 8px;
  cursor: pointer;
`;

const TierRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const TierName = styled.div`
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 900;
`;

const TierPrice = styled.div`
  color: #f6c37e;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
`;

const TierDescription = styled.div`
  color: #cbd3df;
  font-size: 12px;
  line-height: 1.55;
  font-weight: 700;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Pill = styled.span`
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #dfe5ef;
  font-size: 11px;
  font-weight: 800;
`;

const ToggleList = styled.div`
  display: grid;
  gap: 8px;
`;

const ToggleRow = styled.button<{ $active: boolean; $disabled?: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 14px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(86,211,173,.34)' : 'rgba(255,255,255,.08)')};
  background: ${({ $active }) => ($active ? 'rgba(86,211,173,.09)' : 'rgba(255,255,255,.02)')};
  color: inherit;
  padding: 12px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.64 : 1)};
`;

const ToggleCheck = styled.span<{ $active: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(86,211,173,.66)' : 'rgba(255,255,255,.22)')};
  background: ${({ $active }) => ($active ? 'rgba(86,211,173,.18)' : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const ToggleTitle = styled.div`
  color: #f3f4f6;
  font-size: 12px;
  font-weight: 900;
`;

const ToggleCopy = styled.div`
  margin-top: 4px;
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 700;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
`;

const FieldLabel = styled.span`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.22px;
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  min-height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #f3f4f6;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 700;
  box-sizing: border-box;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #f3f4f6;
  padding: 12px;
  font-size: 13px;
  line-height: 1.6;
  font-weight: 700;
  resize: vertical;
  box-sizing: border-box;
`;

const SummaryCard = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 14px;
  display: grid;
  gap: 12px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

const SummaryMetric = styled.div`
  display: grid;
  gap: 4px;
`;

const SummaryMetricLabel = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const SummaryMetricValue = styled.div`
  color: #f3f4f6;
  font-size: 18px;
  line-height: 1;
  font-weight: 900;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FooterNote = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 700;
`;

const ActionButton = styled.button<{ $accent?: boolean }>`
  min-height: 38px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid ${({ $accent }) => ($accent ? 'rgba(224,161,94,.4)' : 'rgba(255,255,255,.14)')};
  background: ${({ $accent }) => ($accent ? 'rgba(224,161,94,.14)' : 'rgba(255,255,255,.04)')};
  color: #f3f4f6;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18px;
  cursor: pointer;

  &:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: #f9b4b4;
  font-size: 12px;
  line-height: 1.55;
  font-weight: 700;
`;

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeCode(value: string | null | undefined) {
  return cleanText(value).toLowerCase();
}

function getOfferingKey(offering: ServiceCatalogOffering, index: number): string {
  return cleanText(offering.id) || cleanText(offering.code) || cleanText(offering.name) || `offering-${index}`;
}

function getAddOnKey(addOn: ServiceCatalogAddOn, index: number): string {
  return cleanText(addOn.skuId) || cleanText(addOn.name) || `addon-${index}`;
}

function formatCurrency(cents: number, currency = 'usd') {
  const amount = Number.isFinite(cents) ? cents / 100 : 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

function describeBillingType(
  billingType: string | null | undefined,
  billingInterval: string | null | undefined,
) {
  const type = normalizeCode(billingType);
  if (type === 'recurring') {
    return billingInterval ? `per ${cleanText(billingInterval)}` : 'recurring';
  }
  if (type === 'one_time') return 'one-time';
  return cleanText(billingType) || 'priced';
}

function computeNextServiceCode(
  services: ServiceCatalogService[],
  initialServiceCode?: string | null,
) {
  const desired = normalizeCode(initialServiceCode);
  if (desired) {
    const matched = services.find((service) => normalizeCode(service.code || service.category) === desired);
    if (matched) return normalizeCode(matched.code || matched.category);
  }
  return normalizeCode(services[0]?.code || services[0]?.category);
}

function matchOfferingIds(
  tier: ServiceCatalogTier | null | undefined,
  labels: string[],
) {
  if (!tier) return [] as string[];
  const wanted = new Set(labels.map((value) => normalizeCode(value)).filter(Boolean));
  if (!wanted.size) return [];
  return tier.offerings
    .map((offering, index) => ({
      key: getOfferingKey(offering, index),
      label: normalizeCode(offering.name || offering.code || offering.id),
    }))
    .filter((entry) => entry.label && wanted.has(entry.label))
    .map((entry) => entry.key);
}

function matchAddOnSkuIds(service: ServiceCatalogService | null | undefined, names: string[]) {
  if (!service) return [] as string[];
  const wanted = new Set(names.map((value) => normalizeCode(value)).filter(Boolean));
  if (!wanted.size) return [];
  return service.addOns
    .map((addOn) => ({
      skuId: cleanText(addOn.skuId),
      label: normalizeCode(addOn.name),
    }))
    .filter((entry) => entry.skuId && entry.label && wanted.has(entry.label))
    .map((entry) => entry.skuId);
}

function buildDefaultProposalTitle(
  service: ServiceCatalogService | null | undefined,
  tier: ServiceCatalogTier | null | undefined,
  proposalType: string,
) {
  const serviceName = cleanText(service?.title) || 'Service';
  const tierName = cleanText(tier?.name) || 'Tier';
  return proposalType === 'change_service'
    ? `${serviceName} / ${tierName} update`
    : `${serviceName} / ${tierName} proposal`;
}

export default function ServiceConfigDrawer({
  open,
  mode,
  orgName,
  services,
  submitting = false,
  submitLabel,
  initialServiceCode,
  initialTierSkuId,
  initialOfferingLabels = [],
  initialAddOnNames = [],
  initialTitle,
  initialSummary,
  initialProposalType,
  onClose,
  onSubmit,
}: ServiceConfigDrawerProps) {
  const availableServices = useMemo(
    () => services.filter((service) => service.checkoutEnabled !== false && service.tiers.length > 0),
    [services],
  );
  const [serviceCode, setServiceCode] = useState('');
  const [tierSkuId, setTierSkuId] = useState('');
  const [selectedOfferingIds, setSelectedOfferingIds] = useState<string[]>([]);
  const [selectedAddOnSkuIds, setSelectedAddOnSkuIds] = useState<string[]>([]);
  const [proposalTitle, setProposalTitle] = useState('');
  const [proposalSummary, setProposalSummary] = useState('');
  const [proposalExpiry, setProposalExpiry] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const nextServiceCode = computeNextServiceCode(availableServices, initialServiceCode);
    const nextService =
      availableServices.find((service) => normalizeCode(service.code || service.category) === nextServiceCode) ??
      availableServices[0] ??
      null;
    const nextTier =
      nextService?.tiers.find((tier) => cleanText(tier.skuId) === cleanText(initialTierSkuId)) ??
      nextService?.tiers[0] ??
      null;
    const preselectedOfferingIds = matchOfferingIds(nextTier, initialOfferingLabels);
    const fallbackOfferingIds =
      preselectedOfferingIds.length > 0
        ? preselectedOfferingIds
        : (nextTier?.offerings ?? []).map((offering, index) => getOfferingKey(offering, index));
    const preselectedAddOns = matchAddOnSkuIds(nextService, initialAddOnNames);
    const defaultExpiry = new Date();
    defaultExpiry.setDate(defaultExpiry.getDate() + 7);

    setServiceCode(nextServiceCode);
    setTierSkuId(cleanText(nextTier?.skuId));
    setSelectedOfferingIds(fallbackOfferingIds);
    setSelectedAddOnSkuIds(preselectedAddOns);
    setProposalTitle(cleanText(initialTitle));
    setProposalSummary(cleanText(initialSummary));
    setProposalExpiry(defaultExpiry.toISOString().slice(0, 10));
    setError(null);
  }, [
    availableServices,
    initialAddOnNames,
    initialOfferingLabels,
    initialServiceCode,
    initialSummary,
    initialTierSkuId,
    initialTitle,
    open,
  ]);

  const selectedService = useMemo(
    () =>
      availableServices.find(
        (service) => normalizeCode(service.code || service.category) === normalizeCode(serviceCode),
      ) ?? null,
    [availableServices, serviceCode],
  );

  useEffect(() => {
    if (!selectedService) return;
    const tierExists = selectedService.tiers.some((tier) => cleanText(tier.skuId) === cleanText(tierSkuId));
    if (!tierExists) {
      setTierSkuId(cleanText(selectedService.tiers[0]?.skuId));
    }
  }, [selectedService, tierSkuId]);

  const selectedTier = useMemo(
    () => selectedService?.tiers.find((tier) => cleanText(tier.skuId) === cleanText(tierSkuId)) ?? null,
    [selectedService, tierSkuId],
  );

  useEffect(() => {
    if (!selectedTier) return;
    const validOfferingIds = new Set(
      selectedTier.offerings.map((offering, index) => getOfferingKey(offering, index)),
    );
    setSelectedOfferingIds((current) => {
      const filtered = current.filter((id) => validOfferingIds.has(id));
      if (filtered.length) return filtered;
      return selectedTier.offerings.map((offering, index) => getOfferingKey(offering, index));
    });
  }, [selectedTier]);

  useEffect(() => {
    if (!selectedService) return;
    const validAddOns = new Set(
      selectedService.addOns.map((addOn) => cleanText(addOn.skuId)).filter(Boolean),
    );
    setSelectedAddOnSkuIds((current) => current.filter((skuId) => validAddOns.has(skuId)));
  }, [selectedService]);

  const requiredOnboardingSkuId = cleanText(selectedTier?.requiredOnboardingSkuId);
  useEffect(() => {
    if (!requiredOnboardingSkuId) return;
    setSelectedAddOnSkuIds((current) =>
      current.includes(requiredOnboardingSkuId) ? current : [...current, requiredOnboardingSkuId],
    );
  }, [requiredOnboardingSkuId]);

  const selectedOfferingLabels = useMemo(() => {
    if (!selectedTier) return [] as string[];
    const selected = new Set(selectedOfferingIds);
    return selectedTier.offerings
      .map((offering, index) => ({
        key: getOfferingKey(offering, index),
        name: cleanText(offering.name || offering.code || offering.id),
      }))
      .filter((offering) => offering.name && selected.has(offering.key))
      .map((offering) => offering.name);
  }, [selectedOfferingIds, selectedTier]);

  const selectedAddOnLabels = useMemo(() => {
    if (!selectedService) return [] as string[];
    const selected = new Set(selectedAddOnSkuIds);
    return selectedService.addOns
      .filter((addOn) => selected.has(cleanText(addOn.skuId)))
      .map((addOn) => cleanText(addOn.name))
      .filter(Boolean);
  }, [selectedAddOnSkuIds, selectedService]);

  const pricingSummary = useMemo(() => {
    const recurringItems: Array<{ amount: number; currency: string }> = [];
    const oneTimeItems: Array<{ amount: number; currency: string }> = [];
    const pushItem = (
      amount: number,
      currency: string,
      billingType: string | null | undefined,
    ) => {
      if (!Number.isFinite(amount) || amount <= 0) return;
      if (normalizeCode(billingType) === 'one_time') {
        oneTimeItems.push({ amount, currency });
      } else {
        recurringItems.push({ amount, currency });
      }
    };

    if (selectedTier) {
      pushItem(
        Number(selectedTier.amountCents || 0),
        cleanText(selectedTier.currency) || 'usd',
        selectedTier.billingType,
      );
    }

    selectedService?.addOns.forEach((addOn) => {
      const skuId = cleanText(addOn.skuId);
      if (!skuId || !selectedAddOnSkuIds.includes(skuId)) return;
      pushItem(
        Number(addOn.amountCents || 0),
        cleanText(addOn.currency) || 'usd',
        addOn.billingType,
      );
    });

    const recurringCurrency = recurringItems[0]?.currency || 'usd';
    const oneTimeCurrency = oneTimeItems[0]?.currency || recurringCurrency;

    return {
      recurring: recurringItems.reduce((sum, item) => sum + item.amount, 0),
      recurringCurrency,
      dueNow: oneTimeItems.reduce((sum, item) => sum + item.amount, 0),
      dueNowCurrency: oneTimeCurrency,
    };
  }, [selectedAddOnSkuIds, selectedService, selectedTier]);

  const proposalType = cleanText(initialProposalType) || (initialTierSkuId ? 'change_service' : 'new_service');
  const generatedProposalTitle = buildDefaultProposalTitle(selectedService, selectedTier, proposalType);

  const offeringsRequired = Boolean(selectedTier?.offerings.length);
  const canSubmit = Boolean(selectedService && selectedTier && (!offeringsRequired || selectedOfferingIds.length > 0));

  const handleToggleOffering = (key: string) => {
    setSelectedOfferingIds((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
    );
  };

  const handleToggleAddOn = (skuId: string) => {
    if (!skuId) return;
    if (requiredOnboardingSkuId && skuId === requiredOnboardingSkuId) return;
    setSelectedAddOnSkuIds((current) =>
      current.includes(skuId) ? current.filter((entry) => entry !== skuId) : [...current, skuId],
    );
  };

  const handleSubmit = async () => {
    setError(null);
    if (!selectedService || !selectedTier) {
      setError('Select a service and tier before continuing.');
      return;
    }
    if (offeringsRequired && selectedOfferingIds.length === 0) {
      setError('Select at least one included offering before continuing.');
      return;
    }

    const tierSku = cleanText(selectedTier.skuId);
    const cartItems = [
      { skuId: tierSku, quantity: 1 },
      ...Array.from(new Set([requiredOnboardingSkuId, ...selectedAddOnSkuIds].filter(Boolean))).map((skuId) => ({
        skuId,
        quantity: 1,
      })),
    ];

    const payload: ServiceConfigSubmission = {
      serviceCategory: cleanText(selectedService.category),
      serviceCode: cleanText(selectedService.code || selectedService.category),
      serviceName: cleanText(selectedService.title),
      tierSkuId: tierSku,
      tierName: cleanText(selectedTier.name),
      cartItems,
      laneSelections: selectedOfferingIds.length
        ? [
            {
              tierSkuId: tierSku,
              laneIds: [...selectedOfferingIds],
              laneLabels: [...selectedOfferingLabels],
            },
          ]
        : undefined,
      configurationSnapshot: {
        serviceCode: cleanText(selectedService.code || selectedService.category),
        tierSkuId: tierSku,
        selectedOfferingLabels,
        selectedAddOnNames: selectedAddOnLabels,
        mode,
      },
    };

    if (mode === 'proposal') {
      payload.title = cleanText(proposalTitle) || generatedProposalTitle;
      payload.summary = cleanText(proposalSummary) || undefined;
      payload.proposalType = proposalType;
      payload.expiresAt = cleanText(proposalExpiry) ? new Date(`${proposalExpiry}T00:00:00`).toISOString() : undefined;
    }

    await onSubmit(payload);
  };

  return (
    <>
      <Overlay type="button" $open={open} onClick={onClose} aria-hidden={!open} />
      <Drawer $open={open}>
        <Header>
          <div>
            <HeaderTitle>
              {mode === 'proposal' ? <CalendarDays size={15} /> : <Sparkles size={15} />}
              {mode === 'proposal' ? 'Create service proposal' : 'Configure service checkout'}
            </HeaderTitle>
            <HeaderSub>
              {mode === 'proposal'
                ? `Build a proposal for ${orgName} using the published service catalog.`
                : `Choose the service, tier, offerings, and add-ons you want to add or change for ${orgName}.`}
            </HeaderSub>
          </div>
          <CloseButton type="button" onClick={onClose}>
            <X size={14} />
          </CloseButton>
        </Header>

        <Body>
          <Section>
            <SectionLabel>Service family</SectionLabel>
            <Tabs>
              {availableServices.map((service) => {
                const code = normalizeCode(service.code || service.category);
                return (
                  <TabButton
                    key={code}
                    type="button"
                    $active={code === normalizeCode(serviceCode)}
                    onClick={() => setServiceCode(code)}
                  >
                    {cleanText(service.title) || cleanText(service.code) || 'Service'}
                  </TabButton>
                );
              })}
            </Tabs>
            {selectedService ? (
              <SectionCopy>
                {cleanText(selectedService.description) || cleanText(selectedService.subtitle) || 'Select the tier that fits this organization.'}
              </SectionCopy>
            ) : null}
          </Section>

          <Section>
            <SectionLabel>Select tier</SectionLabel>
            <TierList>
              {(selectedService?.tiers ?? []).map((tier, index) => {
                const isActive = cleanText(tier.skuId) === cleanText(tierSkuId);
                return (
                  <TierCard
                    key={cleanText(tier.skuId) || cleanText(tier.name) || `tier-${index}`}
                    type="button"
                    $active={isActive}
                    onClick={() => setTierSkuId(cleanText(tier.skuId))}
                  >
                    <TierRow>
                      <TierName>{cleanText(tier.name) || 'Tier'}</TierName>
                      <TierPrice>
                        {formatCurrency(Number(tier.amountCents || 0), tier.currency)} {describeBillingType(tier.billingType, tier.billingInterval)}
                      </TierPrice>
                    </TierRow>
                    {cleanText(tier.description) ? <TierDescription>{cleanText(tier.description)}</TierDescription> : null}
                    <PillRow>
                      {cleanText(tier.billingInterval) ? <Pill>{cleanText(tier.billingInterval)}</Pill> : null}
                      {requiredOnboardingSkuId && cleanText(tier.skuId) === cleanText(tierSkuId) ? <Pill>Includes onboarding</Pill> : null}
                      {tier.offerings.length ? <Pill>{tier.offerings.length} offering{tier.offerings.length === 1 ? '' : 's'}</Pill> : null}
                    </PillRow>
                  </TierCard>
                );
              })}
            </TierList>
          </Section>

          {selectedTier?.offerings.length ? (
            <Section>
              <SectionLabel>Included offerings</SectionLabel>
              <ToggleList>
                {selectedTier.offerings.map((offering, index) => {
                  const key = getOfferingKey(offering, index);
                  const active = selectedOfferingIds.includes(key);
                  return (
                    <ToggleRow key={key} type="button" $active={active} onClick={() => handleToggleOffering(key)}>
                      <ToggleCheck $active={active}>{active ? <Check size={11} /> : null}</ToggleCheck>
                      <div>
                        <ToggleTitle>{cleanText(offering.name || offering.code) || 'Offering'}</ToggleTitle>
                        {cleanText(offering.description) ? <ToggleCopy>{cleanText(offering.description)}</ToggleCopy> : null}
                      </div>
                    </ToggleRow>
                  );
                })}
              </ToggleList>
            </Section>
          ) : null}

          {selectedService?.addOns.length ? (
            <Section>
              <SectionLabel>Add-ons</SectionLabel>
              <ToggleList>
                {selectedService.addOns.map((addOn, index) => {
                  const skuId = cleanText(addOn.skuId);
                  const active = selectedAddOnSkuIds.includes(skuId);
                  const disabled = Boolean(requiredOnboardingSkuId && skuId === requiredOnboardingSkuId);
                  return (
                    <ToggleRow
                      key={getAddOnKey(addOn, index)}
                      type="button"
                      $active={active}
                      $disabled={disabled}
                      onClick={() => handleToggleAddOn(skuId)}
                    >
                      <ToggleCheck $active={active}>{active ? <Check size={11} /> : null}</ToggleCheck>
                      <div>
                        <ToggleTitle>
                          {cleanText(addOn.name) || 'Add-on'}{' '}
                          <span style={{ color: '#f6c37e' }}>
                            {formatCurrency(Number(addOn.amountCents || 0), addOn.currency)} {describeBillingType(addOn.billingType, null)}
                          </span>
                        </ToggleTitle>
                        {disabled ? (
                          <ToggleCopy>Required onboarding is automatically included with this tier.</ToggleCopy>
                        ) : cleanText(addOn.description) ? (
                          <ToggleCopy>{cleanText(addOn.description)}</ToggleCopy>
                        ) : null}
                      </div>
                    </ToggleRow>
                  );
                })}
              </ToggleList>
            </Section>
          ) : null}

          {mode === 'proposal' ? (
            <Section>
              <SectionLabel>Proposal details</SectionLabel>
              <Field>
                <FieldLabel>Proposal title</FieldLabel>
                <Input
                  value={proposalTitle}
                  onChange={(event) => setProposalTitle(event.target.value)}
                  placeholder={generatedProposalTitle}
                />
              </Field>
              <Field>
                <FieldLabel>Summary / client note</FieldLabel>
                <Textarea
                  value={proposalSummary}
                  onChange={(event) => setProposalSummary(event.target.value)}
                  placeholder="Explain what is changing, why it is being proposed, and what the client should expect."
                />
              </Field>
              <Field>
                <FieldLabel>Expires on</FieldLabel>
                <Input
                  type="date"
                  value={proposalExpiry}
                  onChange={(event) => setProposalExpiry(event.target.value)}
                />
              </Field>
            </Section>
          ) : null}

          <SummaryCard>
            <SectionLabel>Configuration snapshot</SectionLabel>
            <SummaryGrid>
              <SummaryMetric>
                <SummaryMetricLabel>Monthly</SummaryMetricLabel>
                <SummaryMetricValue>
                  {formatCurrency(pricingSummary.recurring, pricingSummary.recurringCurrency)}
                </SummaryMetricValue>
              </SummaryMetric>
              <SummaryMetric>
                <SummaryMetricLabel>Due now</SummaryMetricLabel>
                <SummaryMetricValue>
                  {formatCurrency(pricingSummary.dueNow, pricingSummary.dueNowCurrency)}
                </SummaryMetricValue>
              </SummaryMetric>
            </SummaryGrid>
            <PillRow>
              {selectedOfferingLabels.map((label) => (
                <Pill key={label}>{label}</Pill>
              ))}
              {selectedAddOnLabels.map((label) => (
                <Pill key={label}>{label}</Pill>
              ))}
              {!selectedOfferingLabels.length && !selectedAddOnLabels.length ? <Pill>Base tier only</Pill> : null}
            </PillRow>
          </SummaryCard>
        </Body>

        <Footer>
          {error ? <ErrorText>{error}</ErrorText> : null}
          <FooterRow>
            <FooterNote>
              {mode === 'proposal'
                ? 'The backend will validate the exact pricing snapshot before the proposal is saved.'
                : 'Checkout will open in Stripe with the organization context preloaded.'}
            </FooterNote>
            <div style={{ display: 'flex', gap: 8 }}>
              <ActionButton type="button" onClick={onClose} disabled={submitting}>Cancel</ActionButton>
              <ActionButton type="button" $accent onClick={() => void handleSubmit()} disabled={!canSubmit || submitting}>
                {submitting ? 'Working...' : submitLabel || (mode === 'proposal' ? 'Send proposal' : 'Start checkout')}
              </ActionButton>
            </div>
          </FooterRow>
        </Footer>
      </Drawer>
    </>
  );
}
